import { useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppContext } from '../Context';
import { useTranslation } from '../TranslationContext';
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";

const BatchSize = 10;
const NextBatchDelayMs = 500; 

function Gallery() {
	const { psServer, submittedCode, currencySymbol, setShowModal, setModalImageIndex, galleryContainerRef, fullCodeData, gallery, setStartDownloadAll, allowPurchase, setIsLoading, GDPRDaysLeft, setClickedImage, clickedImage, setPackageChoice, onlineOnly } = useAppContext();
	const { t, language } = useTranslation();

	const [showDownload, setShowDownload] = useState(false);
	const [loadedMediaCount, setLoadedMediaCount] = useState(0);
	const [processedGallery, setProcessedGallery] = useState([]);
	
	const [currentBatchEndIndex, setCurrentBatchEndIndex] = useState(0);

	const [showPurchaseModal, setShowPurchaseModal] = useState(false);
	const [selectedPurchasePhoto, setSelectedPurchasePhoto] = useState(null);
	const [showDigOptionOnly, setShowDigOptionOnly] = useState(false);

	const navigate = useNavigate();
	const location = useLocation();
	const [data, setData] = useState([]);
	
	useEffect(() => {
		if (submittedCode === null || submittedCode === "") {
			navigate('/');
		}
	}, [submittedCode, navigate]);

	useEffect(() => {
		if (gallery && gallery.length > 0) {
			const hasBoughtPhoto = gallery.some(photo => photo.bought);
			setShowDownload(hasBoughtPhoto);
		} else {
			setShowDownload(false);
		}
	}, [gallery]);
	
	useEffect(() => {
		if (gallery && gallery.length > 0) {
			setIsLoading(true);
			setLoadedMediaCount(0);
			setCurrentBatchEndIndex(0);
			const newProcessedGallery = gallery.map(photo => {
				const baseSrc = `${psServer}/media/thumb/${submittedCode}/${photo.fileName}/get`;
				const bustedSrc = `${baseSrc}${baseSrc.includes('?') ? '&' : '?'}${Date.now()}`;
				return { ...photo, bustedSrc: bustedSrc };
			});
			setProcessedGallery(newProcessedGallery);
			setCurrentBatchEndIndex(BatchSize); 
		} else if (gallery && gallery.length === 0) {
			setIsLoading(false);
			setProcessedGallery([]);
		}
	}, [gallery, psServer, submittedCode, setIsLoading]);

	useEffect(() => {
		if (processedGallery.length === 0) return;
		const isCurrentBatchLoaded = loadedMediaCount >= Math.min(currentBatchEndIndex, processedGallery.length);
		if (isCurrentBatchLoaded && currentBatchEndIndex < processedGallery.length) {
			const nextBatchStartIndex = currentBatchEndIndex;
			const nextBatchEndIndex = Math.min(nextBatchStartIndex + BatchSize, processedGallery.length);
			const timer = setTimeout(() => {
				setCurrentBatchEndIndex(nextBatchEndIndex);
			}, NextBatchDelayMs);
			return () => clearTimeout(timer);
		}
		if (loadedMediaCount >= processedGallery.length) {
			const timer = setTimeout(() => {
				setIsLoading(false);
			}, 1000);
			return () => clearTimeout(timer);
		}
	}, [loadedMediaCount, processedGallery.length, currentBatchEndIndex, setIsLoading]);

	function handleMediaClick(index) {
		setModalImageIndex(index);
		setShowModal(true);
	}

	function handleLockedPhotoClick(photo=null) {
		if (photo) {
			setSelectedPurchasePhoto(photo);
			setClickedImage(photo.id);
		} else {
			setShowDigOptionOnly(true);
		}
		setTimeout(() => setShowPurchaseModal(true), 300);
	}

	function closePurchaseModal() {
		setShowDigOptionOnly(false);
		setShowPurchaseModal(false);
		setTimeout(() => setSelectedPurchasePhoto(null), 300);
	}
	
	function downloadAll(event) {
		setStartDownloadAll(true);
	}

	function helpPage() {
		window.open('https://www.imageinsight.com/support-cantfind/', '_blank', 'noopener noreferrer');
	}

	const handleClick = (item) => {
		setPackageChoice(item);
		navigate('/checkout');
	};

	const containerVariants = {
		hidden: { opacity: 1 },
		visible: {
			opacity: 1,
		},
	};

	const handleMediaLoad = () => {
		setLoadedMediaCount(prevCount => prevCount + 1);
	};

	function formatDescription(textToConvert) {
		console.log(fullCodeData);
		let theText = textToConvert.replace(`{{v}}`, fullCodeData.photosLocked);
		const lines = theText.split('\n');
		return (
			<div>
				{lines.map((line, index) => (
					<p key={index}>{line}</p>
				))}
			</div>
		);
	}
	useEffect(() => {
		const fetchOnlineOptions = async () => {
			try {
				const method = onlineOnly ? 'single' : 'digital';
				if (psServer && submittedCode) {
					const response = await fetch(`${psServer}/ui/options/${method}/${submittedCode}`, {
						method: 'GET',
						headers: {
							'Content-Type': 'application/json',
						}
					});
					if (!response.ok) throw new Error('Network response was not ok');
					const jsonData = await response.json();
					setData(jsonData);
				}
			} catch (error) {
				console.error('Error fetching data:', error);
			}
		};
		fetchOnlineOptions();
	}, []);
	
	return (
		<>
			<motion.div
				initial={{ scale: 0, opacity: 0 }}
				animate={{ scale: 1, opacity: 1 }}
				transition={{ duration: 0.5, ease: "easeInOut" }}
				className="galleryContainer" ref={galleryContainerRef}
			>
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.5, ease: "easeInOut" }}
					className="buttons"
				>
					{allowPurchase && (
						<button onClick={() => handleLockedPhotoClick()}>{t('buttonGetMore')}</button>
					)}
					{showDownload && (
						<button onClick={downloadAll}>{t('buttonDownload')}</button>
					)}
					<button onClick={helpPage}>{t('buttonHelp')}</button>
				</motion.div>
				<div className="countdownNotice">{t('countDownNotice', {v: GDPRDaysLeft})}</div>
				<motion.div variants={containerVariants} initial="hidden" animate="visible">
					<AnimatePresence>
						<ResponsiveMasonry columnsCountBreakPoints={{ 740: 2, 1110: 3, 1490: 4, 1910: 5, 2460: 6 }} columnClassName="test">
							<Masonry gutter="10px">
								{processedGallery.map((photo, index) => {
									const shouldRetrieve = index < currentBatchEndIndex;
									const srcToUse = shouldRetrieve ? photo.bustedSrc : '';
									
									const onPhotoClick = photo.bought 
										? () => handleMediaClick(index) 
										: () => handleLockedPhotoClick(photo);

									return (
										<React.Fragment key={photo.id}>
											{photo.fileName.toLowerCase().endsWith('.mp4') ? (
												<motion.video
													initial={{ scale: 0, opacity: 0 }}
													whileInView={{ scale: 1, opacity: 1 }}
													whileHover={{ scale: 1.1 }}
													whileTap={{ scale: 0.9 }}
													transition={{ duration: 0.5, ease: "easeInOut" }}
													viewport={{ once: true }}
													muted={true}
													autoPlay={true}
													playsInline={true}
													loop
													preload="auto"
													className={`photo ${photo.bought ? `download` : ''}`}
													id={`photo_${photo.id}`}
													onClick={onPhotoClick}
													style={{
														filter: photo.bought ? 'none' : 'grayscale(100%)'
													}}
													onLoadedData={shouldRetrieve ? handleMediaLoad : undefined} 
												>
													{shouldRetrieve && (
														<source
															src={srcToUse}
															type="video/mp4"
														/>
													)}
												</motion.video>
											) : (
												<motion.img
													initial={{ scale: 0, opacity: 0 }}
													whileInView={{ scale: 1, opacity: 1 }}
													whileHover={{ scale: 1.1, zIndex: 1 }}
													whileTap={{ scale: 0.9 }}
													transition={{ duration: 0.5, ease: "easeInOut" }}
													viewport={{ once: true }}
													src={srcToUse}
													alt=""
													className={`photo ${photo.bought ? `download` : ''}`}
													id={`photo_${photo.id}`}
													onClick={onPhotoClick}
													onLoad={shouldRetrieve ? handleMediaLoad : undefined}
												/>
											)}
										</React.Fragment>
									);
								})}
							</Masonry>
						</ResponsiveMasonry>
					</AnimatePresence>
				</motion.div>
			</motion.div>
			<AnimatePresence>
				{showPurchaseModal && (
					<motion.div 
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="purchaseModalOverlay"
						onClick={closePurchaseModal}
					>
						<motion.div 
							initial={{ scale: 0.8, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							exit={{ scale: 0.8, opacity: 0 }}
							onClick={(e) => e.stopPropagation()} 
							className="purchaseModalContent"
						>
							<div className="purchaseModalItemsWrap">
								{data
									.filter(item => !(showDigOptionOnly && item.alldigital === 0))
									.map((item, index) => (
										<motion.div
											whileHover={{ scale: 1.1 }}
											whileTap={{ 
												scale: 0.9, 
												transition: { duration: 0.5 }
											}}
											className="purchaseModalItem"
											id={item.id}
											key={index}
											style={{ backgroundColor: item.colour }}
											onTap={(event) => {
												handleClick(item);
											}}
										>
											<h1>{JSON.parse(item.name)[language]}</h1>
											<div className="purchaseModalChoiceInfo">{formatDescription(JSON.parse(item.description)[language])}</div>
											{currencySymbol() === "kr" ? (
												<h1>{item.incVat} {currencySymbol()}</h1>
											): (
												<h1>{currencySymbol()}{(item.incVat/100)}</h1>
											)}
										</motion.div>
									)
								)}
							</div>
							<motion.button
								whileHover={{ scale: 1.1 }}
								whileTap={{ 
									scale: 0.9, 
									transition: { duration: 0.5 }
								}}
								className="purchaseModalCancel"
								onClick={closePurchaseModal}
							>
								{t('Cancel') || "Cancel"}
							</motion.button>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>
		</>
	);
}

export default Gallery;